import { Avatar } from '@mantine/core';
import type { Participant } from '../../../types';
import { getAvatarColor, getInitialsFromParts } from '../../../utils/avatarHelpers';
import classes from './UnassignedParticipantCard.module.css';

interface UnassignedParticipantCardProps {
  participant: Participant;
  isSelected: boolean;
  onSelect: () => void;
}

const getGenderLetter = (gender?: string): string | null => {
  if (!gender) return null;
  const lower = gender.toLowerCase();
  if (lower.includes('woman') || lower.includes('female')) return 'F';
  if (lower.includes('man') || lower.includes('male')) return 'M';
  if (lower.includes('non-binary') || lower.includes('nonbinary')) return 'NB';
  return gender.charAt(0).toUpperCase();
};

export function UnassignedParticipantCard({
  participant,
  isSelected,
  onSelect,
}: UnassignedParticipantCardProps) {
  const fullName = `${participant.first_name || ''} ${participant.last_name || ''}`.trim();
  const avatarColor = getAvatarColor(participant.user_id);
  const initials = getInitialsFromParts(participant.first_name, participant.last_name);

  const genderRaw = participant.custom_fields?.gender || participant.custom_fields?.gender_identity || '';
  const genderDisplay = getGenderLetter(genderRaw);
  const personalityDisplay = participant.personality_type || null;

  const cardClass = [classes.card, isSelected ? classes.cardSelected : ''].filter(Boolean).join(' ');

  return (
    <div className={cardClass} onClick={onSelect}>
      {/* Name row */}
      <div className={classes.nameRow}>
        <Avatar
          size={30}
          radius="xl"
          styles={{
            root: { backgroundColor: avatarColor },
            placeholder: { color: '#FFFFFF', backgroundColor: avatarColor },
          }}
        >
          {initials}
        </Avatar>
        <div className={classes.info}>
          <div className={classes.name}>{fullName || participant.email}</div>
          <div className={classes.email}>{participant.email}</div>
        </div>
      </div>

      {/* Meta: gender + personality type */}
      {(genderDisplay || personalityDisplay) && (
        <div className={classes.metaRow}>
          {genderDisplay && <span>{genderDisplay}</span>}
          {genderDisplay && personalityDisplay && <span className={classes.metaSeparator}>·</span>}
          {personalityDisplay && <span>{personalityDisplay}</span>}
        </div>
      )}

      {/* Time slot pills */}
      {participant.available_time_slots && participant.available_time_slots.length > 0 && (
        <div className={classes.slotsRow}>
          {participant.available_time_slots.map((slot) => (
            <span key={slot} className={classes.slotPill}>{slot}</span>
          ))}
        </div>
      )}
    </div>
  );
}
